import { getConfig } from "@stryker/core/api/jobs";
import { QueryKeys } from "@stryker/core/queryKeys";
import type { ComponentMaterialGroup } from "@stryker/core/types/jobs";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export const createEditJobOptionsQuery = queryOptions({
  queryKey: [QueryKeys.JobsConfig],
  queryFn: getConfig,
  staleTime: 1000000,
});
export function useGetConfig() {
  return useQuery({
    ...createEditJobOptionsQuery,
  });
}

type Option = {
  title: number | string;
  value: number | string;
  key?: string;
};
export function useConfigOptions() {
  const { data: config } = useGetConfig();
  const repairSystemOptions = useMemo(
    () =>
      config?.repair_systems?.map((system) => {
        return {
          title: system.name,
          value: system.id,
        };
      }) ?? [],
    [config]
  );

  const surfacePreparationOptions = useMemo(
    () =>
      config?.surface_preparations?.map((surf) => {
        return {
          title: surf.name,
          value: surf.id,
          long_name: surf.long_name,
        };
      }) ?? [],
    [config]
  );

  const temperatureUnitsOptions = useMemo(
    () =>
      config?.temperature_units?.map((temp) => {
        return {
          title: temp,
          value: temp,
        };
      }) ?? [],
    [config]
  );

  const measureUnitOptions = useMemo(
    () =>
      config?.measure_units?.map((unit) => {
        return {
          title: unit,
          value: unit,
        };
      }) ?? [],
    [config]
  );

  const npsOptions = useMemo(
    () =>
      config?.diameter_to_thickness?.map((d) => {
        return {
          title: String(d.nps),
          value: d.nps,
        };
      }) ?? [],
    [config]
  );

  const getScheduleOptions = (nps?: number | string | null): Array<Option> => {
    const id = String(nps);
    if (!id) {
      return [];
    }
    return (
      config?.diameter_to_thickness
        .find((d) => {
          return d.nps === id;
        })
        ?.schedule?.map((t) => {
          return {
            title: t,
            value: t,
          };
        }) ?? []
    );
  };

  const componentMaterialGroups = useMemo<ComponentMaterialGroup[]>(() => {
    return config?.component_material ?? [];
  }, [config?.component_material]);

  const materialOptions = useMemo<Option[]>(() => {
    if (!componentMaterialGroups.length) {
      return [];
    }

    return componentMaterialGroups.flatMap(({ group, options }) => {
      return options.map((entry) => {
        const { id, material, general_material, type } = entry;
        const descriptor = type ?? general_material ?? group;
        const displayLabel = descriptor ? `${material} (${descriptor})` : material;

        return {
          title: displayLabel,
          value: id,
          // Include group in key to avoid collisions across groups
          key: `${group}-${id}`,
        };
      });
    });
  }, [componentMaterialGroups]);

  const componentMaterialLabelById = useMemo<Record<number, string>>(() => {
    return materialOptions.reduce<Record<number, string>>((acc, option) => {
      const id = Number(option.value);
      if (!Number.isNaN(id)) {
        acc[id] = String(option.title);
      }
      return acc;
    }, {});
  }, [materialOptions]);

  const enclosureMaterialLabelById = useMemo<Record<number, string>>(() => {
    const groups = [
      ...(config?.enclosure_shells ?? []),
      ...(config?.shells ?? []),
      ...(config?.endplates ?? []),
      ...(config?.ears ?? []),
      ...(config?.flange_clamps ?? []),
      ...(config?.stud_bolts ?? []),
      ...(config?.strongbacks ?? []),
      ...(config?.strongbacks_stud_bolts ?? []),
    ];

    return groups.reduce<Record<number, string>>((acc, group) => {
      group.options.forEach((option) => {
        const label = option.type ? `${option.material} (${option.type})` : option.material;
        if (!acc[option.id]) {
          acc[option.id] = label;
        }
      });
      return acc;
    }, {});
  }, [
    config?.enclosure_shells,
    config?.shells,
    config?.endplates,
    config?.ears,
    config?.flange_clamps,
    config?.stud_bolts,
    config?.strongbacks,
    config?.strongbacks_stud_bolts,
  ]);

  const getKitOptions = (id: number | null) => {
    return (
      config?.repair_systems
        .find((system) => system.id == id)
        ?.kits?.map((k) => {
          return {
            title: `${k.width}" x ${k.length}'`,
            value: k.id,
          };
        }) ?? []
    );
  };

  const allKits = useMemo(
    () =>
      config?.kits?.map(({ length, width, id }) => {
        return {
          title: `${width}" x ${length}'`,
          value: id,
        };
      }) ?? [],
    [config]
  );
  const jobFlagsOptions = useMemo(
    () =>
      config?.job_flags?.map(({ flag, id }) => {
        return {
          title: flag,
          value: id,
        };
      }) ?? [],
    [config]
  );

  const usersOptions = useMemo(
    () =>
      config?.users?.map(({ email, id }) => {
        return {
          title: email,
          value: id ?? "TODO",
        };
      }) ?? [],
    [config]
  );
  const branchOptions = useMemo(
    () =>
      config?.branch?.map(({ branch }) => {
        return {
          title: branch,
          value: branch || "TODO",
        };
      }) ?? [],
    [config]
  );
  const plantOptions = useMemo(
    () =>
      config?.plant?.map(({ plant }) => {
        return {
          title: plant,
          value: plant,
        };
      }) ?? [],
    [config]
  );

  const repairItemsOptions = useMemo(
    () =>
      config?.repair_items?.map(({ name, id }) => {
        return {
          title: name,
          value: name,
        };
      }) ?? [],
    [config]
  );
  const repairUnitTypesOptions = useMemo(
    () =>
      config?.repair_unit_types?.map((value) => {
        return {
          title: value,
          value: value,
        };
      }) ?? [],
    [config]
  );

  const flangeRatingOptions = useMemo(() => {
    if (!config?.flange_rating) return [];
    const uniqueRatings = [...new Set(config.flange_rating)].sort((a, b) => a - b);
    return uniqueRatings.map((rating) => ({
      title: rating.toString(),
      value: rating,
    }));
  }, [config]);

  const flangeNpsOptions = useMemo(() => {
    if (!config?.flange_nps) return [];
    const uniqueNps = [...new Set(config.flange_nps)].sort((a, b) => a - b);
    return uniqueNps.map((nps) => ({
      title: nps.toString(),
      value: nps,
    }));
  }, [config]);

  return {
    repairUnitTypesOptions,
    repairSystemOptions,
    surfacePreparationOptions,
    temperatureUnitsOptions,
    measureUnitOptions,
    npsOptions,
    componentMaterialGroups,
    materialOptions,
    componentMaterialLabelById,
    enclosureMaterialLabelById,
    getKitOptions,
    getScheduleOptions,
    allKits,
    jobFlagsOptions,
    usersOptions,
    plantOptions,
    branchOptions,
    repairSystems: config?.repair_systems,
    repairItemsOptions,
    flangeRatingOptions,
    flangeNpsOptions,
  };
}
